/**
 * DocumentPicker, pure half.
 *
 * Runs unchanged in the browser and, via the flow-core bundle, inside GraalJS on the backend, so
 * it must never reach for React, the DOM, Node APIs, or the network.
 *
 * Flow JSON 4.0+. Everything below is from Meta's media-upload reference, read 2026-09-24:
 * https://developers.facebook.com/docs/whatsapp/flows/reference/media_upload/
 *
 * The rules that are NOT local to this component, one picker per screen counting PhotoPicker
 * too, the ten-file/100 MiB response budget, and the top-level-payload restriction, live in
 * flowValidation, because no component can see its neighbours or the action that submits it.
 */

import { reservedFieldNameErrors } from '../fieldName';

/** Meta's ceilings on a single DocumentPicker. */
export const MAX_FILE_SIZE_KB = 25600;
export const MAX_UPLOADED_DOCUMENTS = 30;
export const MAX_LABEL = 80;
export const MAX_DESCRIPTION = 300;

/**
 * Every mime type Meta accepts, verbatim. A value outside this list is refused at deploy, and
 * the list is short enough to hold exactly rather than pattern-match.
 *
 * Note Meta's own caveat: older Android and iOS versions do not understand all of these, so a
 * user may still manage to pick something else. The endpoint validates what actually arrives.
 */
export const ALLOWED_MIME_TYPES = [
  'application/gzip',
  'application/msword',
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/vnd.oasis.opendocument.presentation',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/x-7z-compressed',
  'application/zip',
  'image/avif',
  'image/gif',
  'image/heic',
  'image/heif',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/webp',
  'text/plain',
  'video/mp4',
  'video/mpeg',
];

/**
 * What the compiler emits when nobody chose.
 *
 * Meta's own defaults are 30 documents at 25600 KB each, 750 MiB, against a response-message
 * budget of 10 files and 100 MiB. So the defaults violate the limit they are defaulted under,
 * exactly as PhotoPicker's do, and a flow that leaves them alone is refused at PUBLISH rather
 * than at deploy. Capped here instead: 5 files at 8192 KB leaves room for a second picker
 * inside both ceilings, and matches what PhotoPicker settled on for the same reason.
 */
export const DEFAULT_MAX_DOCUMENTS = 5;
export const DEFAULT_MAX_FILE_SIZE_KB = 8192;

const asInt = (value) => {
  if (value === '' || value === undefined || value === null) return null;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? null : parsed;
};

export const DocumentPickerCore = {
  type: 'DocumentPicker',
  name: 'Document Picker',
  icon: '📎',
  description: 'Let the user attach documents, a PDF, a spreadsheet, a photo of a bill.',
  category: 'input',
  // One per screen, but the real rule counts PhotoPicker as well, so flowValidation enforces it
  // across both types. maxPerScreen would only see this one.
  maxPerScreen: 1,
  getDefaultProperties() {
    return {
      name: 'document_picker',
      label: 'Upload documents',
      description: '',
      'allowed-mime-types': [],
      'max-file-size-kb': '',
      'min-uploaded-documents': '',
      'max-uploaded-documents': '',
      enabled: true,
      visible: true,
    };
  },
  getPropertySchema() {
    return {
      name: { type: 'string', label: 'Field Name', required: true },
      label: { type: 'string', label: 'Label', required: true },
      description: { type: 'string', label: 'Description', required: false },
      'allowed-mime-types': {
        type: 'multiselect',
        label: 'Accepted file types',
        required: false,
        options: ALLOWED_MIME_TYPES,
      },
      'max-file-size-kb': { type: 'number', label: 'Max File Size (KB)', required: false },
      'min-uploaded-documents': { type: 'number', label: 'Min Documents', required: false },
      'max-uploaded-documents': { type: 'number', label: 'Max Documents', required: false },
      enabled: { type: 'boolean', label: 'Enabled', required: false },
      visible: { type: 'boolean', label: 'Visible', required: false },
    };
  },
  validateProperties(properties) {
    const errors = [];
    const warnings = [];

    if (!properties.name || properties.name.trim().length === 0) {
      errors.push('Field name is required');
    }
    if (properties.name && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(properties.name)) {
      errors.push('Field name must start with letter or underscore');
    }
    errors.push(...reservedFieldNameErrors(properties.name));

    if (!properties.label || properties.label.trim().length === 0) {
      errors.push('Label is required');
    }
    if (properties.label && properties.label.length > MAX_LABEL) {
      errors.push(`Label must be ${MAX_LABEL} characters or less`);
    }
    if (properties.description && properties.description.length > MAX_DESCRIPTION) {
      errors.push(`Description must be ${MAX_DESCRIPTION} characters or less`);
    }

    const min = asInt(properties['min-uploaded-documents']);
    const max = asInt(properties['max-uploaded-documents']);
    if (min !== null && (min < 0 || min > MAX_UPLOADED_DOCUMENTS)) {
      errors.push(`Min documents must be between 0 and ${MAX_UPLOADED_DOCUMENTS}`);
    }
    if (max !== null && (max < 1 || max > MAX_UPLOADED_DOCUMENTS)) {
      errors.push(`Max documents must be between 1 and ${MAX_UPLOADED_DOCUMENTS}`);
    }
    if (min !== null && max !== null && min > max) {
      errors.push('Min documents cannot be greater than max documents');
    }

    const sizeKb = asInt(properties['max-file-size-kb']);
    if (sizeKb !== null && (sizeKb < 1 || sizeKb > MAX_FILE_SIZE_KB)) {
      errors.push(`Max file size must be between 1 and ${MAX_FILE_SIZE_KB} KB`);
    }

    const mimeTypes = properties['allowed-mime-types'];
    if (mimeTypes !== undefined && mimeTypes !== null && mimeTypes !== '') {
      if (!Array.isArray(mimeTypes)) {
        errors.push('Accepted file types must be a list');
      } else {
        const unknown = mimeTypes.filter((type) => !ALLOWED_MIME_TYPES.includes(type));
        if (unknown.length > 0) {
          errors.push(`WhatsApp does not accept ${unknown.join(', ')}. Choose from the supported file types.`);
        }
      }
    }

    // Not an error, Meta allows it, but a picker nobody can satisfy is worth saying out loud.
    if (min !== null && min > 0 && max !== null && max === min) {
      warnings.push(`This asks for exactly ${min} document${min === 1 ? '' : 's'}, no more and no fewer.`);
    }

    return { errors, warnings };
  },
  transformToWhatsApp(properties) {
    const result = {
      type: 'DocumentPicker',
      name: properties.name,
      label: properties.label,
    };

    if (properties.description) {
      result.description = properties.description;
    }

    const mimeTypes = properties['allowed-mime-types'];
    if (Array.isArray(mimeTypes) && mimeTypes.length > 0) {
      result['allowed-mime-types'] = mimeTypes.filter((type) => ALLOWED_MIME_TYPES.includes(type));
    }

    const min = asInt(properties['min-uploaded-documents']);
    if (min !== null) {
      result['min-uploaded-documents'] = min;
    }

    // Always emitted, and capped, see DEFAULT_MAX_DOCUMENTS. Leaving these out means Meta's
    // own defaults apply, and Meta's own defaults break Meta's own publish-time rule.
    const max = asInt(properties['max-uploaded-documents']);
    result['max-uploaded-documents'] = max === null ? DEFAULT_MAX_DOCUMENTS : Math.min(max, MAX_UPLOADED_DOCUMENTS);

    const sizeKb = asInt(properties['max-file-size-kb']);
    result['max-file-size-kb'] = sizeKb === null ? DEFAULT_MAX_FILE_SIZE_KB : Math.min(sizeKb, MAX_FILE_SIZE_KB);

    if (properties.enabled === false) {
      result.enabled = false;
    }
    if (properties.visible === false) {
      result.visible = false;
    }

    return result;
  },
  transformFromWhatsApp(whatsappComponent) {
    return {
      name: whatsappComponent.name,
      label: whatsappComponent.label,
      description: whatsappComponent.description || '',
      'allowed-mime-types': Array.isArray(whatsappComponent['allowed-mime-types'])
        ? whatsappComponent['allowed-mime-types']
        : [],
      'max-file-size-kb': whatsappComponent['max-file-size-kb'] || '',
      'min-uploaded-documents': whatsappComponent['min-uploaded-documents'] || '',
      'max-uploaded-documents': whatsappComponent['max-uploaded-documents'] || '',
      enabled: whatsappComponent.enabled !== false,
      visible: whatsappComponent.visible !== false,
    };
  },
};
